import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '@context/AuthContext';
import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { db, storage } from '@root/firebase';
import { ref as deleteRef, deleteObject } from 'firebase/storage';
import Layout from '@components/Layout';
import ImageUploader from '@components/ImageUploader';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';

import { toast } from 'react-hot-toast';
import TagInput from '@components/TagInput';

import Tour from '@components/Tour';
import { libraryAdminTourSteps } from '@utils/TourSteps/LibraryAdmin';
import TrashButton from '@components/TrashButton';

function ArticlesAdmin() {
  const { globalUser, globalData, LogError } = useAuth();
  const imageUploaderRef = useRef(null);
  const [libraries, setLibraries] = useState([]);

  const [newLibraryName, setNewLibraryName] = useState('');
  const [newSubLibraryName, setNewSubLibraryName] = useState('');

  const [selectedParent, setSelectedParent] = useState('');
  const [selectedChild, setSelectedChild] = useState('');
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [article, setArticle] = useState('');

  const [tags, setTags] = useState([]); // Track tags
  const [publish, setPublish] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [featured, setFeatured] = useState('');
  const [comments, setComments] = useState('');

  const [isUploading, setIsUploading] = useState(false);
  const docUploaderRef = useRef();

  const [activeAccordion, setActiveAccordion] = useState(null);

  const [paneTitle, setPaneTitle] = useState('Recent Articles');
  const [createBtnArticle, setCreateBtnArticle] = useState(false);

  const [showFuturePublish, setShowFuturePublish] = useState(false);

  const [showArticles, setShowArticles] = useState(true);

  const [recentFiles, setRecentFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 5; // Show 5 articles per page

  const toggleAccordion = (libraryId) => {
    setActiveAccordion((prev) => (prev === libraryId ? null : libraryId));
  };

  const handleToggle = async (fileId, field, currentValue) => {
    const newValue = currentValue === 'Yes' ? 'No' : 'Yes';

    try {
      await updateDoc(doc(db, 'articles', fileId), { [field]: newValue });

      // Update local state immediately for UI responsiveness
      setRecentFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === fileId ? { ...file, [field]: newValue } : file
        )
      );
    } catch (error) {
      console.error(`Error updating ${field} for article ${fileId}:`, error);
    }
  };

  const fetchBreadcrumb = useCallback(async (folderId) => {
    const breadcrumb = [];
    let currentFolderId = folderId;
    while (currentFolderId) {
      const libraryDocRef = doc(db, 'library', currentFolderId);
      const libraryDoc = await getDoc(libraryDocRef);

      if (libraryDoc.exists()) {
        const libraryData = libraryDoc.data();
        breadcrumb.push({
          id: currentFolderId,
          name: libraryData.library_name,
        });
        currentFolderId = libraryData.parent;
      } else {
        console.error(`Library with ID ${currentFolderId} does not exist.`);
        break;
      }
    }
    return breadcrumb.reverse();
  }, []);

  const fetchRecentFilesWithBreadcrumbs = useCallback(async () => {
    try {
      const articlesCollectionRef = collection(db, 'articles');
      const querySnapshot = await getDocs(articlesCollectionRef);

      if (!querySnapshot.empty) {
        const filesArray = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((doc) => doc.title && doc.upload_date && doc.folder_id);

        // Sort articles by upload_date
        const sortedFiles = filesArray.sort(
          (a, b) => new Date(b.upload_date) - new Date(a.upload_date)
        );

        // Fetch additional data: library name, parent folder name & user name
        const filesWithDetails = await Promise.all(
          sortedFiles.map(async (file) => {
            const { libraryName, parentFolderName } = await fetchLibraryDetails(
              file.folder_id
            );
            const userName = await fetchUserName(file.uploaded_by);
            const breadcrumb = await fetchBreadcrumb(file.folder_id);

            return {
              ...file,
              library_name: libraryName,
              parent_folder_name: parentFolderName,
              uploaded_by_name: userName,
              breadcrumb,
            };
          })
        );

        setRecentFiles(filesWithDetails);
      } else {
        console.error('No articles found in the collection.');
      }
    } catch (error) {
      console.error('Error fetching recent files:', error);
    }
  }, [fetchBreadcrumb]);

  useEffect(() => {
    fetchRecentFilesWithBreadcrumbs();
  }, [fetchRecentFilesWithBreadcrumbs]);

  const fetchLibraryDetails = async (folderId) => {
    if (!folderId)
      return {
        libraryName: 'Unknown Library',
        parentFolderName: 'Unknown Parent',
      };

    try {
      const libraryDocRef = doc(db, 'library', folderId);
      const libraryDoc = await getDoc(libraryDocRef);

      if (libraryDoc.exists()) {
        const libraryData = libraryDoc.data();
        const parentFolderName = await fetchParentFolderName(
          libraryData.parent
        );
        return {
          libraryName: libraryData.library_name,
          parentFolderName: parentFolderName,
        };
      }
    } catch (error) {
      console.error(
        `Error fetching library details for folderId ${folderId}:`,
        error
      );
    }
    return {
      libraryName: 'Unknown Library',
      parentFolderName: 'Unknown Parent',
    };
  };

  const fetchParentFolderName = async (parentId) => {
    if (!parentId) return 'Root Folder'; // If no parent, it's a root folder

    try {
      const parentDocRef = doc(db, 'library', parentId);
      const parentDoc = await getDoc(parentDocRef);
      return parentDoc.exists()
        ? parentDoc.data().library_name
        : 'Unknown Parent';
    } catch (error) {
      console.error(
        `Error fetching parent folder name for parentId ${parentId}:`,
        error
      );
      return 'Unknown Parent';
    }
  };

  const fetchUserName = async (uid) => {
    if (!uid) return 'Unknown User';
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      return userDoc.exists() ? userDoc.data().name : 'Unknown User';
    } catch (error) {
      console.error(`Error fetching user name for uid ${uid}:`, error);
      return 'Unknown User';
    }
  };

  useEffect(() => {
    fetchRecentFilesWithBreadcrumbs();
  }, [fetchRecentFilesWithBreadcrumbs]);

  const fetchLibraries = useCallback(async () => {
    try {
      const librarySnapshot = await getDocs(collection(db, 'library'));
      const fileSnapshot = await getDoc(doc(db, 'files', 'library'));

      const libraryList = librarySnapshot.docs.map((doc) => ({
        id: doc.id,
        fileCount: 0, // Default to 0, will update dynamically
        ...doc.data(),
      }));

      const fileData = fileSnapshot.exists() ? fileSnapshot.data() : {};
      const calculateFileCount = (libraryId) => {
        return Object.values(fileData).filter(
          (file) => file.folder_id === libraryId
        ).length;
      };

      const calculateTotalFileCount = (libraryId) => {
        const library = libraryList.find((lib) => lib.id === libraryId);
        if (!library) return 0;
        const childLibraries = libraryList.filter(
          (lib) => lib.parent === libraryId
        );
        return (
          calculateFileCount(libraryId) +
          childLibraries.reduce(
            (count, child) => count + calculateTotalFileCount(child.id),
            0
          )
        );
      };

      const updatedLibraries = libraryList
        .map((library) => ({
          ...library,
          fileCount: calculateFileCount(library.id),
          totalFileCount: calculateTotalFileCount(library.id),
        }))
        .sort((a, b) => a.library_name.localeCompare(b.library_name));

      setLibraries(updatedLibraries);
    } catch (error) {
      console.error('Error fetching libraries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLibraries();
  }, [fetchLibraries]);

  // Filter children based on selected parent
  const childLibraries = libraries.filter(
    (library) => library.parent === selectedParent
  );

  const handleCreateLibrary = async () => {
    if (!newLibraryName.trim()) return;
    try {
      const newLibrary = {
        library_name: newLibraryName.trim(),
        date_created: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, 'library'), newLibrary);
      setLibraries((prev) => [...prev, { id: docRef.id, ...newLibrary }]);
      setNewLibraryName('');
    } catch (error) {
      console.error('Error creating library:', error);
    }
  };

  const handleCreateSubLibrary = async (parentLibraryId) => {
    if (!newSubLibraryName.trim()) return;
    try {
      const newSubLibrary = {
        library_name: newSubLibraryName.trim(),
        parent: parentLibraryId,
        date_created: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, 'library'), newSubLibrary);
      setLibraries((prev) => [...prev, { id: docRef.id, ...newSubLibrary }]);
      setNewSubLibraryName('');
    } catch (error) {
      console.error('Error creating sub-library:', error);
    }
  };

  const fetchAllDescendantFolders = async (parentId, libraries) => {
    const childFolders = libraries.filter((lib) => lib.parent === parentId);
    const descendantIds = [parentId]; // Include the parent folder ID

    for (const folder of childFolders) {
      const childDescendants = await fetchAllDescendantFolders(
        folder.id,
        libraries
      );
      descendantIds.push(...childDescendants);
      console.log('Decendants: ', childDescendants);
    }

    return descendantIds;
  };

  const deleteFolderFiles = async (folderId) => {
    try {
      const libraryDocRef = doc(db, 'files', 'library');
      const libraryDoc = await getDoc(libraryDocRef);
      if (libraryDoc.exists()) {
        const filesData = libraryDoc.data();
        const filesToDelete = Object.keys(filesData).filter(
          (fileId) => filesData[fileId].folder_id === folderId
        );

        for (const fileId of filesToDelete) {
          const file = filesData[fileId];
          const storageRef = deleteRef(
            storage,
            `library/${folderId}/${file.file_name}`
          );
          await deleteObject(storageRef);

          // Remove the file from Firestore
          await updateDoc(libraryDocRef, {
            [fileId]: deleteField(),
          });
        }
      }
    } catch (error) {
      console.error(`Error deleting files for folder ${folderId}:`, error);
    }
  };

  const deleteFolderAndDescendants = async (folderIds) => {
    try {
      for (const folderId of folderIds) {
        // Delete the folder document
        await deleteDoc(doc(db, 'library', folderId));

        // Delete associated files
        await deleteFolderFiles(folderId);
        console.log('Deleted: ', folderId);
      }
    } catch (error) {
      console.error('Error deleting folders and descendants:', error);
    }
  };

  const handleDeleteLibrary = async (parentId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Deleting this folder will also delete all subfolders and files!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        // Fetch all folders
        const querySnapshot = await getDocs(collection(db, 'library'));
        const libraries = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch all descendant folder IDs
        const folderIds = await fetchAllDescendantFolders(parentId, libraries);

        // Delete folders and their files
        await deleteFolderAndDescendants(folderIds);

        // Update UI
        setLibraries((prev) =>
          prev.filter((lib) => !folderIds.includes(lib.id))
        );

        toast.success(
          'Deleted!',
          'The folder and its contents have been deleted.'
        );
      } catch (error) {
        console.error('Error deleting library and its contents:', error);
        Swal.fire(
          'Error!',
          'Failed to delete the folder and its contents.',
          'error'
        );
      }
    }
  };

  const handleFuturePublish = async (value) => {
    value === 'Future'
      ? setShowFuturePublish(true)
      : setShowFuturePublish(false);

    setPublish(value);
  };

  const formValidation = async () => {
    const selectedFile = imageUploaderRef.current?.getSelectedFile();

    // Ensure future publish date is set when required
    if (publish === 'Future' && !publishDate) {
      toast.error('Please select a future publish date.');
      return;
    }

    // Ensure all required fields are filled
    if (
      !selectedParent ||
      (childLibraries.length > 0 && !selectedChild) ||
      !title.trim() ||
      !subTitle.trim() ||
      tags.length === 0 || // Ensure at least one tag is present
      !article.trim() ||
      !publish ||
      typeof featured === 'undefined' || // Ensures 'false' is also checked
      typeof comments === 'undefined' ||
      !selectedFile
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsUploading(true);
    try {
      const folderId = selectedChild || selectedParent;

      // Upload the selected image
      const downloadURL = await imageUploaderRef.current.handleUpload(
        `articles/${title}`,
        selectedFile,
        '',
        ''
      );
      console.log(downloadURL);

      // Prepare the new article entry
      const newFileEntry = {
        title: title.trim(),
        sub_title: subTitle.trim(),
        image_url: downloadURL,
        folder_id: folderId,
        article: article.trim(),
        upload_date: new Date().toISOString(),
        uploaded_by: globalUser?.uid,
        tags: tags,
        publish: publish,
        future_publish_date: publish === 'Future' ? publishDate : null, // Store future date if applicable
        featured: featured,
        comments: comments,
      };

      // Store the article in Firestore
      await addDoc(collection(db, 'articles'), newFileEntry);

      // Refresh the UI after successful upload
      fetchRecentFilesWithBreadcrumbs();

      // Reset form fields
      setSelectedParent('');
      setSelectedChild('');
      setTitle('');
      setSubTitle('');
      setTags([]);
      setArticle('');
      setPublish('');
      setPublishDate('');
      setFeatured(false);
      setComments(false);
      setCreateBtnArticle(false);
      setShowArticles(true);
      setPaneTitle('Recent Articles Added');

      toast.success('Article successfully uploaded!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload the article. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout isAdminPage={true}>
      <Tour steps={libraryAdminTourSteps} pageKey='library-admin' />
      <div className='col-xl-12 col-lg-4'>
        <div className='row'>
          <div className='col-xl-3 col-lg-4'>
            <div className='accordion' id='accordionExample'>
              <div className='accordion-item'>
                <h2 className='accordion-header' id='headingOne'>
                  <button
                    className='accordion-button fw-medium'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target='#collapseOne'
                    aria-expanded='true'
                    aria-controls='collapseOne'
                  >
                    Create New Article
                  </button>
                </h2>
                <div
                  id='collapseOne'
                  className='accordion-collapse collapse show'
                  aria-labelledby='headingOne'
                  data-bs-parent='#accordionExample'
                >
                  <div className='accordion-body'>
                    <div className='text-muted'>
                      <button
                        type='button'
                        className='btn btn-soft-success waves-effect waves-light'
                        onClick={() => {
                          setCreateBtnArticle(true);
                          setShowArticles(false);
                          setPaneTitle('Create New Article');
                        }}
                      >
                        Create Article
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className='accordion-item'>
                <h2 className='accordion-header' id='headingTwo'>
                  <button
                    className='accordion-button fw-medium'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target='#collapseTwo'
                    aria-expanded='true'
                    aria-controls='collapseTwo'
                  >
                    Create New Article Root Folder
                  </button>
                </h2>
                <div
                  id='collapseTwo'
                  className='accordion-collapse collapse'
                  aria-labelledby='headingTwo'
                  data-bs-parent='#accordionExample'
                >
                  <div className='accordion-body'>
                    {/* Create Library Section */}
                    <div className='mb-4'>
                      <div className='d-flex align-items-center'>
                        <div className='col-xl-8'>
                          <input
                            className='form-control'
                            type='text'
                            placeholder='Create Root Library'
                            value={newLibraryName}
                            onChange={(e) => setNewLibraryName(e.target.value)}
                          />
                        </div>
                        <div className='col-xl-4 ms-2'>
                          <button
                            type='button'
                            className='btn btn-soft-success waves-effect waves-light'
                            onClick={handleCreateLibrary}
                          >
                            <i className='mdi mdi-check'></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='accordion-item'>
              <h2 className='accordion-header' id='headingThree'>
                <button
                  className='accordion-button fw-medium'
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target='#collapseThree'
                  aria-expanded='true'
                  aria-controls='collapseThree'
                >
                  Article Folder Listing
                </button>
              </h2>
              <div
                id='collapseThree'
                className='accordion-collapse collapse'
                aria-labelledby='headingThree'
                data-bs-parent='#accordionExample'
              >
                <div className='accordion-body'>
                  <div className='p-4 border-top'>
                    <div className='custom-accordion'>
                      <h6>Library List</h6>
                      <hr />

                      {libraries
                        .filter((library) => !library.parent)
                        .map((library) => (
                          <div
                            key={library.id}
                            className='libraryAccordion mb-2'
                          >
                            <a
                              className='text-body fw-medium py-1 d-flex align-items-center'
                              data-bs-toggle='collapse'
                              href={`#categories-collapse-${library.id}`}
                              role='button'
                              aria-expanded={activeAccordion === library.id}
                              aria-controls={`categories-collapse-${library.id}`}
                              onClick={() => toggleAccordion(library.id)}
                            >
                              <i
                                className={`mdi ${
                                  activeAccordion === library.id
                                    ? 'mdi-folder-open'
                                    : 'mdi-folder'
                                } font-size-20 text-warning me-2`}
                              ></i>
                              {library.library_name}{' '}
                              <i
                                className={`mdi mdi-chevron-${
                                  activeAccordion === library.id ? 'down' : 'up'
                                } accor-down-icon ms-auto`}
                              ></i>
                              <button
                                className='deleteLibrary btn btn-link p-0 ms-2'
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent toggling the accordion when clicking delete
                                  handleDeleteLibrary(library.id);
                                }}
                              >
                                <i className='mdi mdi-trash-can text-danger'></i>
                              </button>
                              <span
                                className={`badge badge-soft-${
                                  library.totalFileCount > 0
                                    ? 'success'
                                    : 'grey'
                                } ms-2`}
                              >
                                {library.totalFileCount}
                              </span>
                            </a>

                            <div
                              className={`collapse ${activeAccordion === library.id ? 'show' : ''}`}
                              id={`categories-collapse-${library.id}`}
                            >
                              <div className='p-2'>
                                <ul className='list-unstyled'>
                                  {libraries
                                    .filter(
                                      (subLibrary) =>
                                        subLibrary.parent === library.id
                                    )
                                    .map((subLibrary) => (
                                      <li
                                        key={subLibrary.id}
                                        className='mb-1 ms-4 d-flex align-items-center'
                                      >
                                        <i className='mdi mdi-folder font-size-16 text-secondary me-2'></i>
                                        <span
                                          onClick={() =>
                                            handleLibraryClick(
                                              subLibrary.id,
                                              subLibrary.library_name
                                            )
                                          }
                                          className='text-body cursor-pointer'
                                        >
                                          {subLibrary.library_name}{' '}
                                        </span>
                                        <button
                                          className='btn btn-link p-0 ms-auto'
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteLibrary(subLibrary.id);
                                          }}
                                        >
                                          <i className='mdi mdi-trash-can text-danger'></i>
                                        </button>

                                        <span
                                          className={`badge badge-soft-${
                                            subLibrary.totalFileCount > 0
                                              ? 'info'
                                              : 'grey'
                                          } ms-2`}
                                        >
                                          {subLibrary.totalFileCount}
                                        </span>
                                      </li>
                                    ))}
                                </ul>

                                {/* Sub-library creation input under the last child */}
                                <div className='d-flex align-items-center mt-2'>
                                  <div className='col-8'>
                                    <input
                                      className='form-control form-control-sm'
                                      type='text'
                                      placeholder='Create Sub Library'
                                      value={newSubLibraryName}
                                      onChange={(e) =>
                                        setNewSubLibraryName(e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className='col-4 ms-2'>
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-soft-success'
                                      onClick={() =>
                                        handleCreateSubLibrary(library.id)
                                      }
                                    >
                                      <i className='mdi mdi-check'></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-xl-9 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent d-flex justify-content-between align-items-center'>
                {/* Left-aligned Title */}
                <h5 className='mb-0'>{paneTitle}</h5>

                {/* Right-aligned Buttons */}
                {createBtnArticle && (
                  <div>
                    <button
                      className='btn btn-dark me-2'
                      onClick={() => {
                        setCreateBtnArticle(false);
                        setShowArticles(true);
                        setPaneTitle('Recent Articles');
                      }}
                    >
                      Close
                    </button>
                    <button className='btn btn-success'>Save</button>
                  </div>
                )}
              </div>

              <div className='card-body'>
                {showArticles &&
                  recentFiles.map((file) => (
                    <div
                      className='popular-product-box rounded my-2'
                      key={file.id}
                    >
                      <div className='d-flex align-items-center'>
                        <div className='flex-shrink-0'>
                          <div className='avatar-md'>
                            <div className='product-img avatar-title img-thumbnail bg-soft-primary border-0'>
                              <img
                                src={file.image_url}
                                className='img-fluid'
                                alt=''
                              />
                            </div>
                          </div>
                        </div>

                        <div className='flex-grow-1 ms-3 overflow-hidden'>
                          <h5 className='mb-1 text-truncate'>
                            <a href='' className='font-size-15 text-dark'>
                              {file.title}
                            </a>
                          </h5>
                          <p className='text-muted font-size-11 fw-semibold mb-0 text-truncate'>
                            {file.parent_folder_name} {' > '}{' '}
                            {file.library_name}
                          </p>
                          <p className='text-muted font-size-11 fw-semibold mb-0 text-truncate'>
                            <strong>Creator:</strong>{' '}
                            <span
                              className='badge rounded-pill bg-primary'
                              data-key='t-hot'
                            >
                              {file.uploaded_by_name}
                            </span>
                            <strong>Created:</strong>{' '}
                            <span
                              className='badge rounded-pill bg-primary'
                              data-key='t-hot'
                            >
                              {new Date(file.upload_date).toLocaleDateString(
                                'en-US',
                                {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric',
                                }
                              )}
                            </span>
                          </p>
                        </div>

                        {/* Right-Aligned Controls */}
                        <div className='d-flex align-items-center ms-auto'>
                          {/* Delete Button */}
                          <div className='me-3'>
                            <TrashButton
                              buttonText='Delete'
                              onDelete={() => handleDeleteFile(file)}
                            />
                          </div>

                          {/* Publish Dropdown */}
                          <div className='btn-group me-3'>
                            <button
                              type='button'
                              className={`btn btn-${file.publish === 'Published' ? 'success' : file.publish === 'Draft' ? 'danger' : 'info'} dropdown-toggle`}
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                            >
                              {file.publish}{' '}
                              <i className='mdi mdi-chevron-down'></i>
                            </button>
                            <div
                              className={`dropdown-menu dropdownmenu-${file.publish === 'Published' ? 'success' : file.publish === 'Draft' ? 'danger' : 'info'}`}
                            >
                              {file.publish !== 'Published' && (
                                <a className='dropdown-item' href='#'>
                                  Publish
                                </a>
                              )}
                              {file.publish !== 'Draft' && (
                                <a className='dropdown-item' href='#'>
                                  Draft
                                </a>
                              )}
                              {file.publish !== 'Future Release' && (
                                <a className='dropdown-item' href='#'>
                                  Future Release
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Comments Toggle */}
                          {/* Comments Toggle */}
                          <div className='tooltip-container d-flex flex-column align-items-center me-3'>
                            <span className='font-size-11 mb-1'>Comments:</span>{' '}
                            {/* Label on top */}
                            <span className='tooltip'>
                              Switch Comments{' '}
                              {file.comments === 'Yes' ? 'Off' : 'On'}
                            </span>
                            <input
                              type='checkbox'
                              id={`comments-${file.id}`}
                              switch='warning'
                              className='form-check-input switch-warning'
                              checked={file.comments === 'Yes'}
                              onChange={() =>
                                handleToggle(file.id, 'comments', file.comments)
                              }
                            />
                            <label
                              htmlFor={`comments-${file.id}`}
                              data-on-label='Yes'
                              data-off-label='No'
                              style={{ cursor: 'pointer' }}
                            ></label>
                          </div>

                          {/* Featured Toggle */}
                          <div className='tooltip-container d-flex flex-column align-items-center'>
                            <span className='font-size-11 mb-1'>Featured:</span>{' '}
                            {/* Label on top */}
                            <span className='tooltip'>
                              {file.featured === 'Yes'
                                ? 'Remove as Featured'
                                : 'Make Featured'}
                            </span>
                            <input
                              type='checkbox'
                              id={`featured-${file.id}`}
                              switch='success'
                              className='form-check-input switch-success'
                              checked={file.featured === 'Yes'}
                              onChange={() =>
                                handleToggle(file.id, 'featured', file.featured)
                              }
                            />
                            <label
                              htmlFor={`featured-${file.id}`}
                              data-on-label='Yes'
                              data-off-label='No'
                              style={{ cursor: 'pointer' }}
                            ></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                {createBtnArticle && (
                  <div className='row'>
                    {/* Left: Image Uploader (Fixed Column) */}
                    <div className='col-3'>
                      <ImageUploader
                        ref={imageUploaderRef}
                        uid={globalUser.uid}
                      />
                    </div>

                    {/* Right: Select Boxes & Titles (9 Columns) */}
                    <div className='col-9'>
                      <div className='row'>
                        {/* Select Parent Library & Article Title */}
                        <div className='col-md-6'>
                          <div className='form-floating mb-3'>
                            <select
                              className='form-select formParent'
                              value={selectedParent}
                              onChange={(e) => {
                                setSelectedParent(e.target.value);
                                setSelectedChild('');
                              }}
                            >
                              <option value=''>Select Parent Library</option>
                              {libraries
                                .filter((library) => !library.parent)
                                .map((library) => (
                                  <option key={library.id} value={library.id}>
                                    {library.library_name}
                                  </option>
                                ))}
                            </select>
                            <label className='form-label'>
                              Select Parent Library
                            </label>
                          </div>
                        </div>

                        <div className='col-md-6'>
                          <div className='form-floating mb-3'>
                            <input
                              className='form-control'
                              type='text'
                              onChange={(e) => setTitle(e.target.value)}
                            />
                            <label className='form-label'>Article Title</label>
                          </div>
                        </div>

                        {/* Select Child Library & Article Subtitle */}
                        <div className='col-md-6'>
                          {selectedParent && childLibraries.length > 0 && (
                            <div className='form-floating mb-3'>
                              <select
                                className='form-select'
                                value={selectedChild}
                                onChange={(e) =>
                                  setSelectedChild(e.target.value)
                                }
                              >
                                <option value=''>Select Child Library</option>
                                {childLibraries.map((library) => (
                                  <option key={library.id} value={library.id}>
                                    {library.library_name}
                                  </option>
                                ))}
                              </select>
                              <label className='form-label'>
                                Select Child Library
                              </label>
                            </div>
                          )}
                        </div>

                        <div className='col-md-6'>
                          <div className='form-floating mb-3'>
                            <input
                              className='form-control'
                              type='text'
                              onChange={(e) => setSubTitle(e.target.value)}
                            />
                            <label className='form-label'>
                              Article Sub Title
                            </label>
                          </div>
                        </div>

                        {/* Meta Tags: Full Width Under Everything */}
                        <div className='col-12'>
                          <div className='form-floating mb-3'>
                            <TagInput tags={tags} onChange={setTags} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Select Child Library & Article Subtitle */}
                    <div
                      className='col-md-12'
                      style={{ width: '100%', overflow: 'hidden' }}
                    >
                      <ReactQuill
                        theme='snow' // Change to "bubble" if you prefer a minimal style
                        value={article} // Controlled component
                        onChange={setArticle}
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }], // Header sizes
                            ['bold', 'italic', 'underline', 'strike'], // Formatting
                            [{ list: 'ordered' }, { list: 'bullet' }], // Lists
                            ['blockquote', 'code-block'], // Block styling
                            [{ script: 'sub' }, { script: 'super' }], // Sub/super scripts
                            [{ indent: '-1' }, { indent: '+1' }], // Indentation
                            [{ align: [] }], // Alignment
                            ['link', 'image', 'video'], // Media
                            ['clean'], // Remove formatting
                          ],
                        }}
                        formats={[
                          'header',
                          'bold',
                          'italic',
                          'underline',
                          'strike',
                          'list',
                          'bullet',
                          'blockquote',
                          'code-block',
                          'script',
                          'indent',
                          'align',
                          'link',
                          'image',
                          'video',
                        ]}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          height: '300px', // Adjust editor height
                          fontSize: '12px', // Increase text size
                          borderRadius: '8px', // Add rounded corners
                          backgroundColor: '#fff', // Light background for better contrast
                        }}
                      />
                    </div>

                    <div className='col-md-2 mt-3'>
                      <div className='form-floating mb-3'>
                        <select
                          className='form-select'
                          onChange={(e) => {
                            handleFuturePublish(e.target.value);
                          }}
                        >
                          <option></option>
                          <option value='Published'>Publish</option>
                          <option value='Draft'>Draft</option>
                          <option value='Future Release'>Future Release</option>
                        </select>
                        <label className='form-label'>Set Publish Status</label>
                      </div>
                    </div>

                    {showFuturePublish && (
                      <div className='col-md-2 mt-3'>
                        <div
                          className='form-floating mb-3'
                          onChange={(e) => {
                            setPublishDate(e.target.value);
                          }}
                        >
                          <input className='form-control' type='date' />
                          <label className='form-label'>Publish Date</label>
                        </div>
                      </div>
                    )}

                    <div className='col-md-2 mt-3'>
                      <div className='form-floating mb-3'>
                        <select
                          className='form-select'
                          onChange={(e) => {
                            setFeatured(e.target.value);
                          }}
                        >
                          <option></option>
                          <option></option>
                          <option value='Yes'>Yes</option>
                          <option value='No'>No</option>
                        </select>
                        <label className='form-label'>Featured</label>
                      </div>
                    </div>

                    <div className='col-md-2 mt-3'>
                      <div className='form-floating mb-3'>
                        <select
                          className='form-select'
                          onChange={(e) => {
                            setComments(e.target.value);
                          }}
                        >
                          <option></option>
                          <option value='Yes'>Yes</option>
                          <option value='No'>No</option>
                        </select>
                        <label className='form-label'>Enable Comments</label>
                      </div>
                    </div>

                    <div className='col-md-4 mt-3 d-flex justify-content-end align-items-center'>
                      <button
                        className='btn btn-dark me-2'
                        onClick={() => {
                          setCreateBtnArticle(false);
                          setShowArticles(true);
                          setPaneTitle('Recent Articles');
                        }}
                      >
                        Close
                      </button>
                      <button
                        className='btn btn-success'
                        onClick={formValidation}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Saving...' : 'Save Article'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ArticlesAdmin;
